import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { useCategories } from "../../hooks/useCategories";

export default function CategoriesPage() {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("popular");
  const { categories, loading } = useCategories();

  const filtered = categories
    .filter((c) => c.name.includes(search) || c.description.includes(search))
    .sort((a, b) => sort === "new" ? (b.isNew ? 1 : -1) : 0);

  return (
    <div className="min-h-screen bg-ivory">
      <Navbar />
      <main className="pt-24 pb-20">
        {/* Header */}
        <section className="bg-deep-green py-16 px-4">
          <div className="max-w-screen-xl mx-auto text-center">
            <p className="font-display italic text-gold text-sm tracking-widest mb-3">Categories</p>
            <h1 className="font-serif text-white text-4xl md:text-5xl font-light mb-4">カテゴリ一覧</h1>
            <p className="text-white/60 text-sm font-sans max-w-xl mx-auto">学びたい技術のカテゴリを選んで、専門の職人・事業者を探しましょう。</p>
          </div>
        </section>

        {/* Controls */}
        <section className="bg-white border-b border-stone-100 sticky top-16 z-30 px-4 py-4">
          <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row gap-3 items-center">
            <div className="relative flex-1 max-w-md">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40 text-sm" />
              <input
                type="text"
                placeholder="カテゴリを検索..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 bg-ivory rounded-full text-sm font-sans text-charcoal outline-none border border-stone-200 focus:border-teal transition-colors"
              />
            </div>
            <div className="flex gap-2 shrink-0">
              {[{ v: "popular", l: "人気順" }, { v: "new", l: "新着" }].map((s) => (
                <button
                  key={s.v}
                  onClick={() => setSort(s.v)}
                  className={`px-4 py-2 rounded-full text-xs font-sans whitespace-nowrap cursor-pointer border transition-all duration-200 ${sort === s.v ? "bg-deep-green text-white border-deep-green" : "bg-white text-charcoal border-stone-200 hover:border-teal"}`}
                >
                  {s.l}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Grid */}
        <section className="px-4 py-12">
          <div className="max-w-screen-xl mx-auto">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                    <div className="h-44 bg-stone-200" />
                    <div className="p-5 space-y-3">
                      <div className="h-4 bg-stone-200 rounded w-3/4" />
                      <div className="h-3 bg-stone-200 rounded" />
                      <div className="h-3 bg-stone-200 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <p className="text-charcoal/50 text-xs font-sans mb-6">{filtered.length}件のカテゴリ</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filtered.map((cat, i) => (
                    <Link
                      key={cat.id}
                      to={`/categories/${cat.id}`}
                      className="group bg-white rounded-2xl overflow-hidden cursor-pointer block card-hover"
                      style={{ animationDelay: `${i * 0.07}s` }}
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-t from-deep-green/70 via-transparent to-transparent" />
                        {cat.isNew && (
                          <span className="absolute top-3 right-3 bg-gold text-white text-xs px-2.5 py-1 rounded-full font-sans">NEW</span>
                        )}
                        <div className="absolute bottom-3 left-3 w-9 h-9 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full">
                          <i className={`${cat.icon} text-white text-lg`} />
                        </div>
                      </div>
                      <div className="p-5">
                        <h3 className="font-serif text-deep-green text-lg font-medium mb-1.5">{cat.name}</h3>
                        <p className="text-charcoal/60 text-xs font-sans leading-relaxed mb-4 line-clamp-2">{cat.description}</p>
                        <div className="flex items-center justify-between text-xs text-charcoal/50 font-sans">
                          <span className="flex items-center gap-1.5">
                            <i className="ri-store-2-line text-teal" />
                            事業者を見る
                          </span>
                          <span className="flex items-center gap-1.5 text-teal">
                            詳細を見る →
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                {filtered.length === 0 && (
                  <div className="text-center py-20">
                    <i className="ri-search-line text-4xl text-charcoal/20 block mb-3" />
                    <p className="text-charcoal/40 font-sans text-sm">「{search}」に一致するカテゴリが見つかりませんでした</p>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
